import React, { useState, useEffect, useRef } from 'react';
import { UseFormRegister, FieldErrors, UseFormTrigger, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ThemedDatePicker } from '../common/themed-datepicker.component';

interface AllowanceInSwedenFormProps {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  trigger: UseFormTrigger<any>;
  setValue: UseFormSetValue<any>;
  isTaxFree: string;
  selectedFiles: File[];
  tripStartDate: string;
  tripEndDate: string;
  travelDays: number;
  allowancePerDay: string;
  total: string;
  fileError?: string;
  onFormChange: (field: string, value: any) => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  formData?: any;
  vatFromPreferences?: number;
}

const AllowanceInSwedenForm: React.FC<AllowanceInSwedenFormProps> = ({
  register,
  errors,
  trigger,
  setValue,
  isTaxFree,
  selectedFiles,
  tripStartDate,
  tripEndDate,
  travelDays,
  allowancePerDay,
  total,
  fileError,
  onFormChange,
  onFileUpload,
  onRemoveFile,
  formData,
  vatFromPreferences
}) => {
  const { t } = useTranslation();
  
  // Dropdown states
  const [isTaxFreeDropdownOpen, setIsTaxFreeDropdownOpen] = useState(false);
  const [showOnInvoiceDropdownOpen, setShowOnInvoiceDropdownOpen] = useState(false);
  const [isPdfAttachedDropdownOpen, setIsPdfAttachedDropdownOpen] = useState(false);
  const [isPartialWorkdayDropdownOpen, setIsPartialWorkdayDropdownOpen] = useState(false);
  const [isFoodAllowanceDropdownOpen, setIsFoodAllowanceDropdownOpen] = useState(false);

  // Refs for click outside
  const taxFreeDropdownRef = useRef<HTMLDivElement>(null);
  const showOnInvoiceDropdownRef = useRef<HTMLDivElement>(null);
  const isPdfAttachedDropdownRef = useRef<HTMLDivElement>(null);
  const isPartialWorkdayDropdownRef = useRef<HTMLDivElement>(null);
  const foodAllowanceDropdownRef = useRef<HTMLDivElement>(null);

  // Options
  const taxFreeOptions = [
    { value: '', label: t('SELECT_OPTION') },
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') }
  ];

  const yesNoOptions = [
    { value: 'yes', label: t('YES') },
    { value: 'no', label: t('NO') }
  ];

  const foodAllowanceOptions = [
    { value: 'no_food_expense', label: t('NO_FOOD_EXPENSE'), name: t('NO_FOOD_EXPENSE'), amount: 0 },
    { value: 'breakfast', label: t('BREAKFAST'), name: t('BREAKFAST_NAME'), amount: 60 },
    { value: 'lunch_or_dinner', label: t('LUNCH_OR_DINNER'), name: t('LUNCH_OR_DINNER_NAME'), amount: 105 },
    { value: 'lunch_and_dinner', label: t('LUNCH_AND_DINNER'), name: t('LUNCH_AND_DINNER_NAME'), amount: 210 },
    { value: 'breakfast_lunch_and_dinner', label: t('BREAKFAST_LUNCH_AND_DINNER'), name: t('BREAKFAST_LUNCH_AND_DINNER_NAME'), amount: 270 },
  ];

  const normalizeFoodAllowance = (value: any): string => {
    const validKeys = ['no_food_expense', 'breakfast', 'lunch_or_dinner', 'lunch_and_dinner', 'breakfast_lunch_and_dinner'];
    if (typeof value === 'string' && validKeys.includes(value)) return value;
    const amountToKey: Record<number, string> = { 0: 'no_food_expense', 60: 'breakfast', 105: 'lunch_or_dinner', 210: 'lunch_and_dinner', 270: 'breakfast_lunch_and_dinner' };
    const num = parseFloat(value);
    return amountToKey[num] || 'no_food_expense';
  };
  const currentFoodAllowance = normalizeFoodAllowance(formData?.foodAllowance);

  const foodAllowanceAmount = foodAllowanceOptions.find(o => o.value === currentFoodAllowance)?.amount ?? 0;
  const netTotal = (parseFloat(total || '0') - (travelDays * foodAllowanceAmount)).toFixed(2);

  // Helper functions
  const getTaxFreeLabel = (value: string) => {
    if (!value || value === '') return t('SELECT_OPTION');
    return value === 'yes' ? t('YES') : t('NO');
  };

  const getYesNoLabel = (value: string) => {
    return value === 'yes' ? t('YES') : t('NO');
  };

  const getPartialWorkdayLabel = (value: string) => {
    if (!value || value === '') return t('SELECT_OPTION');
    return value === 'yes' ? t('YES') : t('NO');
  };

  const getFoodAllowanceLabel = (value: string) => {
    const option = foodAllowanceOptions.find(o => o.value === value);
    return option ? option.label : t('SELECT_OPTION');
  };

  const getFoodAllowanceName = (value: string) => {
    const option = foodAllowanceOptions.find(o => o.value === value);
    return option ? option.name : '';
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (taxFreeDropdownRef.current && !taxFreeDropdownRef.current.contains(event.target as Node)) {
        setIsTaxFreeDropdownOpen(false);
      }
      if (showOnInvoiceDropdownRef.current && !showOnInvoiceDropdownRef.current.contains(event.target as Node)) {
        setShowOnInvoiceDropdownOpen(false);
      }
      if (isPdfAttachedDropdownRef.current && !isPdfAttachedDropdownRef.current.contains(event.target as Node)) {
        setIsPdfAttachedDropdownOpen(false);
      }
      if (isPartialWorkdayDropdownRef.current && !isPartialWorkdayDropdownRef.current.contains(event.target as Node)) {
        setIsPartialWorkdayDropdownOpen(false);
      }
      if (foodAllowanceDropdownRef.current && !foodAllowanceDropdownRef.current.contains(event.target as Node)) {
        setIsFoodAllowanceDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Convert datetime string to date format for HTML input
  const formatDateForInput = (dateStr: string) => {
    if (!dateStr) return '';
    // Handle ISO format "2025-12-02T00:00:00" or already formatted "2025-12-02"
    return dateStr.split('T')[0];
  };

  const validateEndDate = (endDate: string) => {
    if (!tripStartDate || !endDate) return true;
    return new Date(endDate) >= new Date(tripStartDate) || t('END_DATE_AFTER_START');
  };

  return (
    <>
      {/* Row 1: Is Tax Free & Show on Invoice */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('IS_ALLOWANCE_SWEDEN_TAX_FREE')}</label>
          <div ref={taxFreeDropdownRef} style={{ position: 'relative' }}>
            <input type="hidden" {...register('isTaxFree', { required: t('PLEASE_SELECT_OPTION') })} />
            <div
              onClick={() => setIsTaxFreeDropdownOpen(!isTaxFreeDropdownOpen)}
              style={{
                height: '46px',
                border: errors.isTaxFree ? '1px solid #dc3545' : '1px solid #eaebf0',
                borderRadius: '8px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: '#fff',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '15px',
                fontWeight: 500,
                color: (!isTaxFree || isTaxFree === '') ? '#68727d' : '#252525',
                boxShadow: isTaxFreeDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                outline: isTaxFreeDropdownOpen ? '1px solid #7c3aed' : 'none'
              }}
            >
              <span>{getTaxFreeLabel(isTaxFree)}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  transform: isTaxFreeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {isTaxFreeDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: '#fff',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  overflow: 'hidden',
                  padding: '4px 0'
                }}
              >
                {taxFreeOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      onFormChange('isTaxFree', option.value);
                      trigger('isTaxFree');
                      setIsTaxFreeDropdownOpen(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      margin: '0 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#252525',
                      backgroundColor: isTaxFree === option.value ? '#f4efff' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (isTaxFree !== option.value) {
                        e.currentTarget.style.backgroundColor = '#f4efff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isTaxFree !== option.value) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          {errors.isTaxFree && (
            <div className="error-message">{errors.isTaxFree.message?.toString()}</div>
          )}
        </div>

        {/* Show on Invoice - Only if isTaxFree is NOT 'no' */}
        {isTaxFree !== 'no' && (
          <div className="form-group">
            <label>{t('SHOW_ON_INVOICE_QUESTION')}</label>
            <div ref={showOnInvoiceDropdownRef} style={{ position: 'relative' }}>
              <input type="hidden" {...register('showOnInvoice')} />
              <div
                onClick={() => setShowOnInvoiceDropdownOpen(!showOnInvoiceDropdownOpen)}
                style={{
                  height: '46px',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  padding: '0 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  backgroundColor: '#fff',
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#252525',
                  boxShadow: showOnInvoiceDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                  outline: showOnInvoiceDropdownOpen ? '1px solid #7c3aed' : 'none'
                }}
              >
                <span>{getYesNoLabel(formData?.showOnInvoice || 'yes')}</span>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  style={{
                    transform: showOnInvoiceDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                >
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              {showOnInvoiceDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: '#fff',
                    border: '1px solid #eaebf0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    zIndex: 1000,
                    overflow: 'hidden',
                    padding: '4px 0'
                  }}
                >
                  {yesNoOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        onFormChange('showOnInvoice', option.value);
                        trigger('showOnInvoice');
                        setShowOnInvoiceDropdownOpen(false);
                      }}
                      style={{
                        padding: '12px 16px',
                        margin: '0 8px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontFamily: 'Plus Jakarta Sans, sans-serif',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: '#252525',
                        backgroundColor: (formData?.showOnInvoice || 'yes') === option.value ? '#f4efff' : '#fff',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if ((formData?.showOnInvoice || 'yes') !== option.value) {
                          e.currentTarget.style.backgroundColor = '#f4efff';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if ((formData?.showOnInvoice || 'yes') !== option.value) {
                          e.currentTarget.style.backgroundColor = '#fff';
                        }
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Upload File (PDF only) - Full Width */}
      <div className="form-group">
        <label>{t('UPLOAD_FILE_PDF_1MB')}</label>
        <div className="file-upload-wrapper">
          <div 
            className="file-upload-area"
            onClick={() => document.getElementById('file-upload-allowance-in-pdf')?.click()}
            style={{ cursor: 'pointer' }}
          >
            <button 
              type="button" 
              className="browse-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {selectedFiles.length > 0 ? t('CHANGE_PDF_FILE') : t('UPLOAD_PDF_FILE')}
            </button>
            <input 
              type="file" 
              id="file-upload-allowance-in-pdf" 
              accept=".pdf,application/pdf" 
              onChange={onFileUpload}
              onClick={(e) => { (e.target as HTMLInputElement).value = ''; }}
              className="file-input"
              style={{ display: 'none' }}
            />
          </div>
          {selectedFiles.length > 0 && (
            <div className="uploaded-files">
              {selectedFiles.map((file, index) => (
                <div key={index} className="file-tag">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                    <a
                    href={URL.createObjectURL(file)}
                    download={file.name}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      color: '#7c3aed',
                    }}
                  >
                    {file.name}
                  </a>
                  <span style={{ marginLeft: '8px', color: '#888', fontSize: '12px' }}>
                    ({(file.size / 1024).toFixed(2)} {t('KB')})
                  </span>
                  <button type="button" className="remove-btn" onClick={() => onRemoveFile(index)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        {fileError && (
          <div className="error-message" style={{ marginTop: '8px' }}>{fileError}</div>
        )}
      </div>

      {/* Show PDF to Customer - Always Visible */}
      {selectedFiles.length > 0 && (
        <div className="form-group">
          <label>{t('SHOW_PDF_TO_CUSTOMER')}</label>
          <div ref={isPdfAttachedDropdownRef} style={{ position: 'relative' }}>
            <input type="hidden" {...register('isPdfAttachedToInvoice')} />
            <div
              onClick={() => setIsPdfAttachedDropdownOpen(!isPdfAttachedDropdownOpen)}
              style={{
                height: '46px',
                border: '1px solid #eaebf0',
                borderRadius: '8px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: '#fff',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '15px',
                fontWeight: 500,
                color: '#252525',
                boxShadow: isPdfAttachedDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                outline: isPdfAttachedDropdownOpen ? '1px solid #7c3aed' : 'none'
              }}
            >
              <span>{getYesNoLabel(formData?.isPdfAttachedToInvoice || 'yes')}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  transform: isPdfAttachedDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {isPdfAttachedDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: '#fff',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  overflow: 'hidden',
                  padding: '4px 0'
                }}
              >
                {yesNoOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      onFormChange('isPdfAttachedToInvoice', option.value);
                      trigger('isPdfAttachedToInvoice');
                      setIsPdfAttachedDropdownOpen(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      margin: '0 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#252525',
                      backgroundColor: (formData?.isPdfAttachedToInvoice || 'yes') === option.value ? '#f4efff' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if ((formData?.isPdfAttachedToInvoice || 'yes') !== option.value) {
                        e.currentTarget.style.backgroundColor = '#f4efff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if ((formData?.isPdfAttachedToInvoice || 'yes') !== option.value) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Row 2: Partial Workday & Trip Started Date */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('PARTIAL_WORKDAY_QUESTION')}</label>
          <div ref={isPartialWorkdayDropdownRef} style={{ position: 'relative' }}>
            <input type="hidden" {...register('isPartialWorkday', { required: t('PLEASE_SELECT_OPTION') })} />
            <div
              onClick={() => setIsPartialWorkdayDropdownOpen(!isPartialWorkdayDropdownOpen)}
              style={{
                height: '46px',
                border: errors.isPartialWorkday ? '1px solid #dc3545' : '1px solid #eaebf0',
                borderRadius: '8px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                backgroundColor: '#fff',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: '15px',
                fontWeight: 500,
                color: (!formData?.isPartialWorkday || formData?.isPartialWorkday === '') ? '#68727d' : '#252525',
                boxShadow: isPartialWorkdayDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
                outline: isPartialWorkdayDropdownOpen ? '1px solid #7c3aed' : 'none'
              }}
            >
              <span>{getPartialWorkdayLabel(formData?.isPartialWorkday || '')}</span>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                style={{
                  transform: isPartialWorkdayDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {isPartialWorkdayDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: '#fff',
                  border: '1px solid #eaebf0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  overflow: 'hidden',
                  padding: '4px 0'
                }}
              >
                {taxFreeOptions.map((option) => (
                  <div
                    key={option.value}
                    onClick={() => {
                      onFormChange('isPartialWorkday', option.value);
                      if (option.value === 'yes') {
                        onFormChange('foodAllowance', 'no_food_expense');
                      }
                      trigger('isPartialWorkday');
                      setIsPartialWorkdayDropdownOpen(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      margin: '0 8px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontSize: '15px',
                      fontWeight: 500,
                      color: '#252525',
                      backgroundColor: formData?.isPartialWorkday === option.value ? '#f4efff' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (formData?.isPartialWorkday !== option.value) {
                        e.currentTarget.style.backgroundColor = '#f4efff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (formData?.isPartialWorkday !== option.value) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
          {errors.isPartialWorkday && (
            <div className="error-message">{errors.isPartialWorkday.message?.toString()}</div>
          )}
        </div>

        <div className="form-group">
          <label>{t('TRIP_STARTED_DATE')}</label>
          <ThemedDatePicker
            value={formatDateForInput(formData?.tripStartDate || tripStartDate)}
            onChange={(v) => {
              onFormChange('tripStartDate', v);
              setValue('tripStartDate', v, { shouldValidate: true });
              trigger('tripStartDate');
            }}
            hasError={!!errors.tripStartDate}
          />
          <input type="hidden" {...register('tripStartDate', { required: t('PLEASE_SELECT_START_DATE') })} />
          {errors.tripStartDate && (
            <div className="error-message">{errors.tripStartDate.message?.toString()}</div>
          )}
        </div>
      </div>

      {/* Row 3: Trip Ended Date & Reason For Trip */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('TRIP_ENDED_DATE')}</label>
          <ThemedDatePicker
            value={formatDateForInput(formData?.tripEndDate || tripEndDate)}
            onChange={(v) => {
              onFormChange('tripEndDate', v);
              setValue('tripEndDate', v, { shouldValidate: true });
              trigger('tripEndDate');
            }}
            hasError={!!errors.tripEndDate}
          />
          <input type="hidden" {...register('tripEndDate', { required: t('PLEASE_SELECT_END_DATE'), validate: validateEndDate })} />
          {errors.tripEndDate && (
            <div className="error-message">{errors.tripEndDate.message?.toString()}</div>
          )}
        </div>

        <div className="form-group">
          <label>{t('REASON_FOR_TRIP')}</label>
          <input
            type="text"
            {...register('reason', { required: t('PLEASE_ENTER_REASON') })}
            className={`form-control ${errors.reason ? 'error' : ''}`}
            placeholder={t('WRITE_HERE')}
            onChange={(e) => {
              onFormChange('reason', e.target.value);
              trigger('reason');
            }}
          />
          {errors.reason && (
            <div className="error-message">{errors.reason.message?.toString()}</div>
          )}
        </div>
      </div>

      {/* Row 4: Country & Destination */}
      <div className="form-row">
        <div className="form-group">
          <label>{t('COUNTRY')}</label>
          <input
            type="text"
            {...register('country')}
            className="form-control"
            value={t('SWEDEN')}
            disabled
            style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
          />
        </div>

        <div className="form-group">
          <label>{t('DESTINATION')}</label>
          <input
            type="text"
            {...register('destination', { required: t('PLEASE_ENTER_DESTINATION') })}
            className={`form-control ${errors.destination ? 'error' : ''}`}
            placeholder={t('WRITE_HERE')}
            onChange={(e) => {
              onFormChange('destination', e.target.value);
              trigger('destination');
            }}
          />
          {errors.destination && (
            <div className="error-message">{errors.destination.message?.toString()}</div>
          )}
        </div>
      </div>

      {/* Food Expense Dropdown - Full Width */}
      {formData?.isPartialWorkday !== 'yes' && (
      <div className="form-group">
        <label>{t('FOOD_EXPENSE')}</label>
        <div ref={foodAllowanceDropdownRef} style={{ position: 'relative' }}>
          <div
            onClick={() => setIsFoodAllowanceDropdownOpen(!isFoodAllowanceDropdownOpen)}
            style={{
              height: '46px',
              border: '1px solid #eaebf0',
              borderRadius: '8px',
              padding: '0 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              backgroundColor: '#fff',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              fontSize: '15px',
              fontWeight: 500,
              color: !currentFoodAllowance ? '#68727d' : '#252525',
              boxShadow: isFoodAllowanceDropdownOpen ? '0 0 0 0.2rem rgba(103, 80, 184, 0.15)' : 'none',
              outline: isFoodAllowanceDropdownOpen ? '1px solid #7c3aed' : 'none'
            }}
          >
            <span>{getFoodAllowanceLabel(currentFoodAllowance)}</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              style={{
                transform: isFoodAllowanceDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s'
              }}
            >
              <path d="M5 7.5L10 12.5L15 7.5" stroke="#252525" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          {isFoodAllowanceDropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                backgroundColor: '#fff',
                border: '1px solid #eaebf0',
                borderRadius: '8px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                overflow: 'hidden',
                padding: '4px 0'
              }}
            >
              {foodAllowanceOptions.map((option) => (
                <div
                  key={option.value}
                  onClick={() => {
                    onFormChange('foodAllowance', option.value);
                    setIsFoodAllowanceDropdownOpen(false);
                  }}
                  style={{
                    padding: '12px 16px',
                    margin: '0 8px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: '15px',
                    fontWeight: 500,
                    color: '#252525',
                    backgroundColor: currentFoodAllowance === option.value ? '#f4efff' : '#fff',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (currentFoodAllowance !== option.value) {
                      e.currentTarget.style.backgroundColor = '#f4efff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentFoodAllowance !== option.value) {
                      e.currentTarget.style.backgroundColor = '#fff';
                    }
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Summary Section */}
      <div className="summary-section">
        <h6>{t('SUMMARY')}</h6>
        <div className="form-row">
          <div className="form-group">
            <label>{t('TRAVEL_DAYS')}:</label>
            <input
              type="text"
              className="form-control"
              value={travelDays}
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>

          <div className="form-group">
            <label>{t('ALLOWANCE_PER_DAY')}:</label>
            <input
              type="text"
              className="form-control"
              value={`${allowancePerDay} ${t('SEK')}`}
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>
        </div>

        {foodAllowanceAmount > 0 && (
          <div className="form-row">
            <div className="form-group">
              <label>{t('FOOD_EXPENSE')}:</label>
              <input
                type="text"
                className="form-control"
                value={`${getFoodAllowanceName(currentFoodAllowance)}: -${travelDays * foodAllowanceAmount} ${t('SEK')}`}
                disabled
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            </div>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label>{t('TOTAL')}:</label>
            <input
              type="text"
              className="form-control"
              value={`${netTotal} ${t('SEK')}`}
              disabled
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>
        </div>

        {/* VAT Info Display */}
        {vatFromPreferences !== undefined && (
          <div className="form-group">
            <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', fontSize: '14px', color: '#666', marginTop: '12px' }}>
              <strong>{t('VAT')}:</strong> {vatFromPreferences}% {t('FROM_INVOICE_PREFERENCES') || '(from invoice preferences)'}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AllowanceInSwedenForm;
